/**
 * CoverArtCanvas. Wraps a background image and overlay layers in a
 * container sized exactly like the image's rendered rect at
 * `object-fit: contain`. The whole artwork is always visible; the
 * wrapper letterboxes on whichever axis doesn't match the image's
 * aspect ratio (the letterbox area shows whatever the parent's
 * background color is underneath).
 *
 * Children positioned in % land at the same point on the artwork
 * regardless of viewport size, because the wrapper IS the image rect.
 *
 * Use for any overlay that needs to track painted features in the
 * background art (candles, pinned UI marks, scene-specific decals).
 *
 *   <CoverArtCanvas src="/store-bg.webp" aspectRatio={1672/941}>
 *     <CandleFlames candles={...} />
 *   </CoverArtCanvas>
 */
export default function CoverArtCanvas({
  src,
  aspectRatio,
  imageStyle,
  className,
  style,
  children,
  wrapperRef,
}) {
  return (
    <div
      ref={wrapperRef}
      className={className}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        // Contain math: pick the SMALLER of "viewport-width worth of
        // height" or "viewport-height" so the wrapper never exceeds
        // the viewport on either axis. aspect-ratio derives the
        // matching width. The image stays fully visible at every
        // window size. The mismatched axis letterboxes to the
        // parent's background color.
        height: `min(100cqh, calc(100cqw / ${aspectRatio}))`,
        aspectRatio: String(aspectRatio),
        pointerEvents: 'none',
        ...style,
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            display: 'block',
            ...imageStyle,
          }}
        />
      ) : null}
      {children}
    </div>
  );
}
